// ============================================================
// AKROSH R M — Portfolio Data
// ============================================================

export const METRICS = [
  {
    val: 40, suffix: '%',
    label: 'DB Stability Improved',
    desc: 'PostgreSQL connection pooling on AWS RDS during peak traffic',
    color: '#FF9900',
  },
  {
    val: 30, suffix: 'k+',
    label: 'Concurrent Users',
    desc: 'Real-time notifications via EventBridge + SNS + SES pipeline',
    color: '#00D8FF',
  },
  {
    val: 500, suffix: 'k+',
    label: 'Records Processed',
    desc: 'Streaming Excel export — zero timeouts, memory-efficient',
    color: '#4A90C4',
  },
  {
    val: 70, suffix: '%',
    label: 'Setup Effort Reduced',
    desc: 'Super Admin one-click AWS environment automation tool',
    color: '#10B981',
  },
];

export const STACK_LAYERS = [
  {
    label: 'Frontend Layer',
    color: '#00D8FF',
    items: [
      { icon: '⚛', name: 'React.js', desc: 'Component-driven UI', sub: 'MUI · Tailwind CSS · Responsive Architecture' },
      { icon: '📱', name: 'React Native', desc: 'Cross-platform mobile', sub: 'Learning · iOS & Android' },
    ],
  },
  {
    label: 'Backend Layer',
    color: '#10B981',
    items: [
      { icon: '🟢', name: 'Node.js v20+', desc: 'REST APIs & real-time', sub: 'Express.js · WebSockets · Socket.io · Streaming' },
      { icon: '🐍', name: 'Python', desc: 'AI / Data pipelines', sub: 'Learning · FastAPI · ML' },
    ],
  },
  {
    label: 'AWS Cloud Layer',
    color: '#FF9900',
    items: [
      { icon: 'λ', name: 'Lambda', desc: 'Serverless compute', sub: 'Event-driven · Auto-scaling · Cold-start optimized' },
      { icon: '🔀', name: 'API Gateway', desc: 'REST + WebSocket APIs', sub: 'Throttling · Auth · CORS · Stage management' },
      { icon: '⚡', name: 'EventBridge', desc: 'Event bus — 30k+ concurrent', sub: 'Rules · Schedules · Event patterns' },
      { icon: '📬', name: 'SNS + SES', desc: 'Notifications at scale', sub: 'Push · Email automation · SMS' },
      { icon: '☁', name: 'S3 + CloudFront', desc: 'CDN + Object storage', sub: 'Static hosting · Media delivery · Cache' },
      { icon: '🔐', name: 'Cognito', desc: 'Auth + SSO', sub: 'JWT · OAuth · MFA · Social login' },
    ],
  },
  {
    label: 'Database Layer',
    color: '#4A90C4',
    items: [
      { icon: '🐘', name: 'PostgreSQL v16', desc: 'Primary data store', sub: 'Indexing strategies · Connection pooling · Query optimization' },
      { icon: '🗄', name: 'AWS RDS', desc: 'Managed relational DB', sub: 'Multi-AZ · Automated backups · Read replicas' },
    ],
  },
  {
    label: 'Infra & DevOps',
    color: '#F59E0B',
    items: [
      { icon: '🛡', name: 'AWS VPC + WAF', desc: 'Network security', sub: 'Subnets · Security groups · Rate limiting' },
      { icon: '🚀', name: 'CI/CD + Vercel', desc: 'Deploy pipelines', sub: 'GitHub Actions · Auto-deploy · Environment management' },
    ],
  },
];

export const JOBS = [
  {
    company: 'Excelsoft Technologies',
    role: 'Full Stack Developer',
    period: 'Dec 2022 – Present',
    type: 'Full-time',
    location: 'Gulbarga, India',
    color: '#FF9900',
    icon: '🏢',
    highlights: [
      { icon: '🗄', text: 'Stabilized AWS RDS during peak onboarding traffic with advanced PostgreSQL connection pooling and optimized query execution — improved DB reliability by 40%' },
      { icon: '⚡', text: 'Architected event-driven notification pipeline (EventBridge + SNS + SES) delivering real-time alerts to 30,000+ concurrent users with high throughput and fault tolerance' },
      { icon: '📊', text: 'Engineered streaming Excel export of 500,000+ database records — eliminated all timeout failures with memory-efficient techniques' },
      { icon: '🤖', text: "Built 'Super Admin' automation tool provisioning complete AWS environments (DB schemas, S3, Cognito, CloudFront) in one click — reduced manual setup by 70%" },
      { icon: '🏗', text: 'Migrated legacy backend workflows to AWS Well-Architected standards — improved scalability, operational excellence, and system observability' },
    ],
    tech: ['React.js', 'Node.js', 'AWS Lambda', 'PostgreSQL', 'EventBridge', 'SNS/SES', 'S3', 'Cognito', 'CloudFront', 'VPC', 'WAF'],
  },
  {
    company: 'Freelance — HRM Application',
    role: 'Full Stack Developer',
    period: '2024 – Present',
    type: 'Freelance',
    location: 'Remote',
    color: '#00D8FF',
    icon: '👥',
    highlights: [
      { icon: '👤', text: 'Building full-stack Human Resource Management system — employee onboarding, attendance tracking, leave management, and offboarding workflows' },
      { icon: '💰', text: 'Payroll processing engine with dynamic salary structures, deductions, and automated pay slip generation using AWS Lambda' },
      { icon: '🔐', text: 'Role-based access control (RBAC) with HR Admin, Manager, and Employee tiers using AWS Cognito identity management' },
      { icon: '📊', text: 'Real-time HR analytics dashboard — headcount, attrition, attendance rates built with React.js + PostgreSQL aggregations' },
      { icon: '📬', text: 'Automated notifications for leave approvals, payroll milestones, and onboarding events via AWS SES' },
    ],
    tech: ['React.js', 'Node.js', 'PostgreSQL RDS', 'AWS Lambda', 'Cognito', 'SES', 'S3', 'API Gateway'],
  },
  {
    company: 'Freelance — CRM Application',
    role: 'Full Stack Developer',
    period: '2024 – Present',
    type: 'Freelance',
    location: 'Remote',
    color: '#4A90C4',
    icon: '💼',
    highlights: [
      { icon: '🤝', text: 'Building Customer Relationship Management system — lead tracking, pipeline management, deal stages from prospect to closed-won' },
      { icon: '📞', text: 'Contact management with full activity timeline — calls, emails, meetings, notes with complete audit trail in PostgreSQL' },
      { icon: '📈', text: 'Sales pipeline dashboard with funnel analytics, conversion rates, and revenue forecasting using React.js + Chart.js' },
      { icon: '⚡', text: 'Real-time lead status notifications and follow-up reminders via AWS EventBridge + SNS push notifications' },
      { icon: '🔗', text: 'RESTful API backend on AWS Lambda + API Gateway with JWT authentication, rate limiting, and request validation' },
    ],
    tech: ['React.js', 'Node.js', 'AWS Serverless', 'PostgreSQL', 'EventBridge', 'SNS', 'API Gateway', 'CloudFront'],
  },
];

export const PROJECTS = [
  {
    name: 'HRM Application',
    tag: 'Freelance',
    category: 'Freelance',
    color: '#00D8FF',
    icon: '👥',
    desc: 'Full-stack Human Resource Management system — employee lifecycle, payroll engine, attendance, leave management, and HR analytics dashboard.',
    highlights: ['Employee onboarding + offboarding flows', 'Automated payroll engine on Lambda', 'RBAC: Admin / Manager / Employee', 'HR analytics dashboard'],
    tech: ['React.js', 'Node.js', 'PostgreSQL', 'Lambda', 'Cognito', 'SES'],
    status: 'Active',
  },
  {
    name: 'CRM Application',
    tag: 'Freelance',
    category: 'Freelance',
    color: '#4A90C4',
    icon: '💼',
    desc: 'Customer Relationship Management — lead pipeline, contact activity timeline, sales funnel analytics, and real-time EventBridge notifications.',
    highlights: ['Drag-and-drop lead pipeline stages', 'Contact activity timeline in PostgreSQL', 'Revenue forecasting charts', 'EventBridge follow-up alerts'],
    tech: ['React.js', 'Node.js', 'PostgreSQL', 'EventBridge', 'SNS', 'API Gateway'],
    status: 'Active',
  },
  {
    name: 'NYSDOS',
    tag: 'US Client',
    category: 'Excelsoft',
    color: '#FF9900',
    icon: '🏛',
    desc: 'Secure backend architecture and REST API optimization for NY State Dept of State — low-latency international data access across distributed users.',
    highlights: ['Secure REST API architecture', 'Cross-region low-latency access', 'PostgreSQL query optimization', 'VPC + WAF security layer'],
    tech: ['Node.js', 'PostgreSQL', 'AWS RDS', 'API Gateway', 'VPC', 'WAF'],
    status: 'Shipped',
  },
  {
    name: 'EnablED / Excellearn',
    tag: 'Ed-Tech',
    category: 'Excelsoft',
    color: '#10B981',
    icon: '🎓',
    desc: 'Gamification-driven learning platform — dynamic assessments, learning paths, and automated email campaigns using AWS SES.',
    highlights: ['Gamification engine + XP system', 'Dynamic assessment builder', 'AWS SES email automation', 'Adaptive learning paths'],
    tech: ['React.js', 'Node.js', 'PostgreSQL', 'AWS SES', 'Lambda', 'S3'],
    status: 'Shipped',
  },
  {
    name: 'ICICIPru Integration',
    tag: 'Enterprise',
    category: 'Excelsoft',
    color: '#F59E0B',
    icon: '🏦',
    desc: 'Enterprise Admin Dashboard with SSO and bulk data ingestion pipeline for ICICI Prudential — 500k+ record processing without timeouts.',
    highlights: ['Enterprise SSO implementation', 'Bulk Lambda data ingestion', '500k+ record pipeline', 'Admin role management'],
    tech: ['React.js', 'Node.js', 'Lambda', 'PostgreSQL', 'Cognito SSO'],
    status: 'Shipped',
  },
  {
    name: 'Super Admin Tool',
    tag: 'Internal',
    category: 'Excelsoft',
    color: '#FFBD59',
    icon: '⚙',
    desc: 'One-click AWS environment provisioner — automatically creates DB schemas, S3 buckets, Cognito pools, and CloudFront distributions.',
    highlights: ['1-click full AWS env setup', 'Auto DB schema provisioning', 'S3 + Cognito + CloudFront', '70% setup time reduction'],
    tech: ['Node.js', 'AWS SDK', 'Lambda', 'CloudFormation', 'S3', 'Cognito'],
    status: 'Shipped',
  },
  {
    name: 'AssistantAI Chatbot',
    tag: 'AI',
    category: 'Excelsoft',
    color: '#8B5CF6',
    icon: '🤖',
    desc: 'Real-time AI chat interface with streaming response architecture and WebSockets for low-latency interactions.',
    highlights: ['WebSocket streaming responses', 'Real-time token streaming UI', 'React.js chat interface', 'Low-latency API Gateway'],
    tech: ['React.js', 'WebSockets', 'Node.js', 'Lambda', 'API Gateway'],
    status: 'Shipped',
  },
];

export const SKILLS = [
  { name: 'React.js',         years: '3.3', pct: 90, color: '#00D8FF' },
  { name: 'Node.js v20+',     years: '3.3', pct: 88, color: '#10B981' },
  { name: 'AWS Lambda',       years: '3',   pct: 87, color: '#FF9900' },
  { name: 'PostgreSQL v16',   years: '3',   pct: 85, color: '#4A90C4' },
  { name: 'API Gateway',      years: '3',   pct: 85, color: '#FF9900' },
  { name: 'EventBridge+SNS',  years: '2.5', pct: 82, color: '#FFBD59' },
  { name: 'S3 + CloudFront',  years: '3',   pct: 82, color: '#FF9900' },
  { name: 'AWS Cognito',      years: '2.5', pct: 80, color: '#F59E0B' },
  { name: 'WebSockets',       years: '2',   pct: 78, color: '#00D8FF' },
  { name: 'VPC + WAF',        years: '2',   pct: 75, color: '#10B981' },
  { name: 'CI/CD + Vercel',   years: '2',   pct: 75, color: '#F59E0B' },
  { name: 'Python',           years: '0.5', pct: 40, color: '#8B5CF6' },
  { name: 'React Native',     years: '0.5', pct: 35, color: '#00D8FF' },
  { name: 'AI / ML',          years: '0.3', pct: 30, color: '#8B5CF6' },
];
